/**
 * DraggableDeliveryTimeline - Timeline com suporte a Drag & Drop
 * Permite reordenar paradas manualmente arrastando
 *
 * Features:
 * - Drag and drop usando @dnd-kit
 * - Feedback visual durante arraste
 * - Callback para mudanca de ordem
 * - Animacoes suaves
 */

import { memo, useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Flag as StartIcon,
  FlagOutlined as EndIcon,
  Schedule as TimeIcon,
  Timer as ServiceTimeIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { formatArrivalTime, formatDuration } from '../../common/services/routingService';
import {
  STOP_STATUS, getStatusConfig, getPriorityConfig,
} from './DeliveryTimeline';

// Item arrastavel da timeline
const SortableTimelineItem = memo(({
  step,
  index,
  isFirst,
  isLast,
  isActive,
  routeColor,
  showConnector,
  onClick,
  tokens,
  isDraggable,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id, disabled: !isDraggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const statusConfig = getStatusConfig(step.status, tokens);
  const priorityConfig = getPriorityConfig(step.priority, tokens);
  const StatusIcon = statusConfig.icon;

  const isStartOrEnd = step.type === 'start' || step.type === 'end';

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        position: 'relative',
        minHeight: isStartOrEnd ? 48 : 80,
        backgroundColor: isDragging ? tokens?.surface?.elevated : 'transparent',
        borderRadius: isDragging ? tokens?.radius?.lg : 0,
      }}
    >
      {/* Coluna da timeline (indicador + linha) */}
      <Box
        sx={{
          width: 48,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {/* Linha conectora superior */}
        {!isFirst && showConnector && (
          <Box
            sx={{
              width: 2,
              height: 12,
              backgroundColor: step.status === STOP_STATUS.COMPLETED
                ? statusConfig.color
                : tokens?.border?.subtle,
            }}
          />
        )}

        {/* Indicador circular */}
        <Box
          sx={{
            width: isStartOrEnd ? 28 : 32,
            height: isStartOrEnd ? 28 : 32,
            borderRadius: '50%',
            backgroundColor: isStartOrEnd
              ? (step.type === 'start' ? tokens?.semantic?.success : tokens?.text?.tertiary)
              : statusConfig.bgColor,
            border: `2px solid ${isStartOrEnd
              ? (step.type === 'start' ? tokens?.semantic?.success : tokens?.text?.tertiary)
              : statusConfig.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.2s ease',
            ...(statusConfig.pulse && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { boxShadow: `0 0 0 0 ${statusConfig.color}40` },
                '70%': { boxShadow: `0 0 0 8px ${statusConfig.color}00` },
                '100%': { boxShadow: `0 0 0 0 ${statusConfig.color}00` },
              },
            }),
          }}
        >
          {isStartOrEnd ? (
            step.type === 'start' ? (
              <StartIcon sx={{ color: '#fff', fontSize: 14 }} />
            ) : (
              <EndIcon sx={{ color: '#fff', fontSize: 14 }} />
            )
          ) : step.status === STOP_STATUS.PENDING ? (
            <Typography
              sx={{
                color: routeColor || statusConfig.color,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {index}
            </Typography>
          ) : (
            <StatusIcon sx={{ color: statusConfig.color, fontSize: 16 }} />
          )}
        </Box>

        {/* Linha conectora inferior */}
        {!isLast && showConnector && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 20,
              backgroundColor: step.status === STOP_STATUS.COMPLETED
                ? statusConfig.color
                : tokens?.border?.subtle,
            }}
          />
        )}
      </Box>

      {/* Conteudo do item */}
      <Box
        onClick={() => onClick?.(step)}
        sx={{
          flex: 1,
          py: 1,
          pl: 1,
          pr: 1,
          cursor: onClick ? 'pointer' : 'default',
          borderRadius: tokens?.radius?.md,
          backgroundColor: isActive ? `${routeColor || statusConfig.color}10` : 'transparent',
          border: isActive ? `1px solid ${routeColor || statusConfig.color}` : '1px solid transparent',
          transition: 'all 0.15s ease',
          '&:hover': onClick ? {
            backgroundColor: tokens?.surface?.hover,
          } : {},
        }}
      >
        {isStartOrEnd ? (
          // Start/End simples
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
              {step.type === 'start' ? 'Inicio da rota' : 'Fim da rota'}
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
              {formatArrivalTime(step.arrival || 0)}
            </Typography>
          </Box>
        ) : (
          // Parada de entrega
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {/* Handle de arraste */}
            {isDraggable && (
              <Box
                {...attributes}
                {...listeners}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'grab',
                  color: tokens?.text?.tertiary,
                  padding: '4px 0',
                  '&:hover': {
                    color: tokens?.text?.secondary,
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <DragIcon sx={{ fontSize: 18 }} />
              </Box>
            )}

            {/* Conteudo principal */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Linha superior: Nome + Prioridade + Horario */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      sx={{
                        color: tokens?.text?.primary,
                        fontSize: '13px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.name || `Entrega #${step.id}`}
                    </Typography>

                    {/* Badge de prioridade alta */}
                    {priorityConfig.showBadge && (
                      <Tooltip title={`Prioridade ${priorityConfig.label}`}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: `${priorityConfig.color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {priorityConfig.icon && (
                            <priorityConfig.icon sx={{ color: priorityConfig.color, fontSize: 12 }} />
                          )}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Endereco */}
                  {step.address && (
                    <Typography
                      sx={{
                        color: tokens?.text?.tertiary,
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mt: 0.25,
                      }}
                    >
                      {step.address}
                    </Typography>
                  )}
                </Box>

                {/* Horarios */}
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                    <TimeIcon sx={{ fontSize: 12, color: tokens?.text?.tertiary }} />
                    <Typography
                      sx={{
                        color: tokens?.text?.primary,
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      {formatArrivalTime(step.arrival || 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Linha inferior: Status + Tempo de servico */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={statusConfig.label}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '10px',
                    fontWeight: 500,
                    backgroundColor: statusConfig.bgColor,
                    color: statusConfig.color,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />

                {step.serviceTime > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <ServiceTimeIcon sx={{ fontSize: 11, color: tokens?.text?.tertiary }} />
                    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '10px' }}>
                      {Math.round(step.serviceTime / 60)} min
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
});

SortableTimelineItem.displayName = 'SortableTimelineItem';

// Item de overlay durante arraste
const DragOverlayItem = memo(({ step, index, routeColor, tokens }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        backgroundColor: tokens?.surface?.elevated,
        borderRadius: tokens?.radius?.lg,
        border: `2px solid ${routeColor || tokens?.accent?.primary}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        minWidth: 280,
      }}
    >
      {/* Numero */}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: routeColor || tokens?.accent?.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>
          {index}
        </Typography>
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            color: tokens?.text?.primary,
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {step.name || `Entrega #${step.id}`}
        </Typography>
        {step.address && (
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
            {step.address}
          </Typography>
        )}
      </Box>

      {/* Handle */}
      <DragIcon sx={{ color: tokens?.text?.tertiary, fontSize: 20 }} />
    </Box>
  );
});

DragOverlayItem.displayName = 'DragOverlayItem';

// Componente principal
const DraggableDeliveryTimeline = memo(({
  steps = [],
  routeColor,
  activeStepId,
  onStepClick,
  onOrderChange,
  showConnector = true,
  vehicleName,
  totalDistance,
  totalDuration,
  disabled = false,
}) => {
  const tokens = usePremiumTokens();
  const [activeId, setActiveId] = useState(null);

  // Separar steps em draggables (jobs) e fixos (start/end)
  const { startStep, endStep, jobSteps } = useMemo(() => {
    const start = steps.find((s) => s.type === 'start');
    const end = steps.find((s) => s.type === 'end');
    const jobs = steps
      .filter((s) => s.type === 'job')
      .sort((a, b) => (a.optimizedOrder || a.arrival || 0) - (b.optimizedOrder || b.arrival || 0));

    return { startStep: start, endStep: end, jobSteps: jobs };
  }, [steps]);

  // IDs para sortable context
  const jobIds = useMemo(() => jobSteps.map((s) => s.id), [jobSteps]);

  // Sensores para drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimo de pixels para iniciar drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handlers de drag
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = jobSteps.findIndex((s) => s.id === active.id);
      const newIndex = jobSteps.findIndex((s) => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(jobSteps, oldIndex, newIndex);

        // Chamar callback com nova ordem
        if (onOrderChange) {
          // Atualizar optimizedOrder de cada step
          const updatedSteps = newOrder.map((step, idx) => ({
            ...step,
            optimizedOrder: idx + 1,
          }));

          onOrderChange(updatedSteps);
        }
      }
    }
  };

  // Item ativo para overlay
  const activeStep = activeId ? jobSteps.find((s) => s.id === activeId) : null;
  const activeIndex = activeStep ? jobSteps.indexOf(activeStep) + 1 : 0;

  // Estatisticas
  const completedCount = jobSteps.filter((s) => s.status === STOP_STATUS.COMPLETED).length;
  const skippedCount = jobSteps.filter((s) => s.status === STOP_STATUS.SKIPPED).length;
  const pendingCount = jobSteps.length - completedCount - skippedCount;

  return (
    <Box>
      {/* Header com resumo */}
      {(vehicleName || totalDistance || totalDuration) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            pb: 1.5,
            borderBottom: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          {vehicleName && (
            <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
              {vehicleName}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${completedCount} concluidas`}
              size="small"
              sx={{
                height: 20,
                fontSize: '10px',
                backgroundColor: `${tokens?.semantic?.success}20`,
                color: tokens?.semantic?.success,
              }}
            />
            {pendingCount > 0 && (
              <Chip
                label={`${pendingCount} pendentes`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: tokens?.surface?.elevated,
                  color: tokens?.text?.secondary,
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Instrucao de drag */}
      {!disabled && jobSteps.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            px: 1,
          }}
        >
          <DragIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
            Arraste as paradas para reordenar
          </Typography>
        </Box>
      )}

      {/* Timeline com DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Start (fixo) */}
          {startStep && (
            <SortableTimelineItem
              step={startStep}
              index={null}
              isFirst
              isLast={!jobSteps.length && !endStep}
              isActive={false}
              routeColor={routeColor}
              showConnector={showConnector}
              onClick={onStepClick}
              tokens={tokens}
              isDraggable={false}
            />
          )}

          {/* Jobs (draggables) */}
          <SortableContext items={jobIds} strategy={verticalListSortingStrategy}>
            {jobSteps.map((step, idx) => (
              <SortableTimelineItem
                key={step.id}
                step={step}
                index={idx + 1}
                isFirst={!startStep && idx === 0}
                isLast={!endStep && idx === jobSteps.length - 1}
                isActive={activeStepId === step.id}
                routeColor={routeColor}
                showConnector={showConnector}
                onClick={onStepClick}
                tokens={tokens}
                isDraggable={!disabled && step.status !== STOP_STATUS.COMPLETED}
              />
            ))}
          </SortableContext>

          {/* End (fixo) */}
          {endStep && (
            <SortableTimelineItem
              step={endStep}
              index={null}
              isFirst={!startStep && !jobSteps.length}
              isLast
              isActive={false}
              routeColor={routeColor}
              showConnector={showConnector}
              onClick={onStepClick}
              tokens={tokens}
              isDraggable={false}
            />
          )}
        </Box>

        {/* Overlay durante arraste */}
        <DragOverlay>
          {activeStep ? (
            <DragOverlayItem
              step={activeStep}
              index={activeIndex}
              routeColor={routeColor}
              tokens={tokens}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer com totais */}
      {(totalDistance || totalDuration) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
            pt: 1.5,
            borderTop: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          {totalDistance && (
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
              Total: {typeof totalDistance === 'number'
                ? `${(totalDistance / 1000).toFixed(1)} km`
                : totalDistance}
            </Typography>
          )}
          {totalDuration && (
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
              {typeof totalDuration === 'number'
                ? formatDuration(totalDuration)
                : totalDuration}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
});

DraggableDeliveryTimeline.displayName = 'DraggableDeliveryTimeline';

export default DraggableDeliveryTimeline;
